/**
 * Forum Store Service
 * 
 * Public API client for forum features in the storefront.
 */

import { apiClient } from '../api/client'

// ========== Types ==========

export interface ForumCategory {
  id: string
  name: string
  slug: string
  description: string
  icon: string
  order: number
  isActive: boolean
  boards: ForumBoard[]
  createdAt: string
}

export interface ForumBoard {
  id: string
  categoryId: string
  name: string
  slug: string
  description: string
  icon: string
  color: string
  order: number
  isLocked: boolean
  topicsCount: number
  postsCount: number
  lastPost?: {
    id: string
    content: string
    authorName: string
    createdAt: string
  }
}

export interface ForumTopic {
  id: string
  boardId: string
  authorId: string
  authorName: string
  authorHytaleNick: string
  title: string
  slug: string
  isPinned: boolean
  isLocked: boolean
  views: number
  postsCount: number
  reactionsCount: number
  createdAt: string
  updatedAt: string
  lastPostAt: string
}

export interface ForumPost {
  id: string
  topicId: string
  authorId: string
  authorName: string
  authorHytaleNick: string
  authorRole?: string
  authorAvatar?: string
  authorBadges: ForumBadge[]
  content: string
  reactions: PostReaction[]
  isEdited?: boolean
  editedAt?: string
  createdAt: string
  updatedAt: string
}

export interface ForumBadge {
  id: string
  name: string
  description: string
  icon: string
  type: string
  color: string
  style?: string
  animation?: string
}

export interface PostReaction {
  type: 'LIKE' | 'LOVE' | 'HAHA' | 'WOW' | 'SAD' | 'ANGRY'
  count: number
  users: ReactionUser[]
}

export interface ReactionUser {
  playerId: string
  playerName: string
  playerNick: string
}

export interface ForumNotification {
  id: string
  type: 'NEW_POST' | 'REPLY' | 'MENTION' | 'REACTION' | 'BADGE_EARNED'
  title: string
  message: string
  isRead: boolean
  link: string
  createdAt: string
}

export interface CreateTopicDTO {
  boardId: string
  title: string
  content: string
}

export interface CreatePostDTO {
  topicId: string
  content: string
}

export interface ForumStats {
  usersOnline: number
  totalTopics: number
  totalPosts: number
  newestMember: string | null
}

// ========== Service ==========

const forumStoreService = {
  // Categories & Boards
  async listCategories(storeId: string): Promise<ForumCategory[]> {
    const response = await apiClient.get<{ categories: ForumCategory[] }>(`/public/stores/${storeId}/forum/categories`)
    return response.categories
  },

  async getBoardBySlug(storeId: string, categorySlug: string, boardSlug: string): Promise<ForumBoard> {
    const response = await apiClient.get<{ board: ForumBoard }>(`/public/stores/${storeId}/forum/categories/${categorySlug}/boards/${boardSlug}`)
    return response.board
  },

  // Topics
  async listTopics(storeId: string, boardId: string, page = 1): Promise<{ topics: ForumTopic[]; total: number }> {
    const response = await apiClient.get<{ topics: ForumTopic[]; total: number }>(`/stores/${storeId}/forum/topics/board/${boardId}`, {
      params: { page, perPage: 20 },
    })
    return response
  },

  async getTopicBySlug(
    storeId: string,
    categorySlug: string,
    boardSlug: string,
    topicSlug: string,
    skipViewIncrement = false,
  ): Promise<ForumTopic> {
    const params = skipViewIncrement ? { skipViewIncrement: 'true' } : {}
    const response = await apiClient.get<{ topic: ForumTopic }>(
      `/public/stores/${storeId}/forum/categories/${categorySlug}/boards/${boardSlug}/topics/${topicSlug}`,
      { params },
    )
    return response.topic
  },

  async createTopic(storeId: string, data: CreateTopicDTO): Promise<ForumTopic> {
    const response = await apiClient.post<{ topic: ForumTopic }>(`/stores/${storeId}/forum/topics`, data)
    return response.topic
  },

  // Posts
  async listPosts(storeId: string, topicId: string, page = 1): Promise<{ posts: ForumPost[]; total: number }> {
    const response = await apiClient.get<{ posts: ForumPost[]; total: number }>(`/stores/${storeId}/forum/posts/topic/${topicId}`, {
      params: { page, perPage: 20 },
    })
    return response
  },

  async createPost(storeId: string, data: CreatePostDTO): Promise<ForumPost> {
    const response = await apiClient.post<{ post: ForumPost }>(`/stores/${storeId}/forum/posts`, data)
    return response.post
  },

  // Reactions
  async reactToPost(storeId: string, postId: string, type: string): Promise<void> {
    await apiClient.post(`/stores/${storeId}/forum/reactions`, { postId, type })
  },

  async removeReaction(storeId: string, postId: string): Promise<void> {
    await apiClient.delete(`/stores/${storeId}/forum/reactions/${postId}`)
  },

  // Notifications
  async listNotifications(): Promise<ForumNotification[]> {
    const response = await apiClient.get<{ notifications: ForumNotification[] }>('/forum/notifications')
    return response.notifications
  },

  async markNotificationAsRead(notificationId: string): Promise<void> {
    await apiClient.patch(`/forum/notifications/${notificationId}/read`)
  },

  async markAllNotificationsAsRead(): Promise<void> {
    await apiClient.patch('/forum/notifications/read-all')
  },

  async getUnreadCount(): Promise<number> {
    const response = await apiClient.get<{ count: number }>('/forum/notifications/unread-count')
    return response.count
  },

  // Statistics
  async getForumStats(storeId: string): Promise<ForumStats> {
    const response = await apiClient.get<ForumStats>(`/public/stores/${storeId}/forum/stats`)
    return response
  },

  async getOnlineUsers(storeId: string): Promise<Array<{ 
    id: string; 
    name: string; 
    nick: string;
    roleName: string;
    badges?: Array<{
      id: string;
      name: string;
      icon: string;
      color: string;
      style?: string;
      animation?: string;
    }>;
  }>> {
    const response = await apiClient.get<{ users: Array<{ 
      id: string; 
      name: string; 
      nick: string;
      roleName: string;
      badges?: Array<{
        id: string;
        name: string;
        icon: string;
        color: string;
        style?: string;
        animation?: string;
      }>;
    }> }>(
      `/public/stores/${storeId}/forum/online-users`
    )
    return response.users
  },

  async getRecentActivity(storeId: string): Promise<Array<{ id: string; type: 'new_topic' | 'new_post' | 'reaction'; user: string; action: string; time: string }>> {
    const response = await apiClient.get<{ activities: Array<{ id: string; type: 'new_topic' | 'new_post' | 'reaction'; user: string; action: string; time: string }> }>(
      `/public/stores/${storeId}/forum/recent-activity`
    )
    return response.activities
  },

  async getTopContributors(storeId: string): Promise<Array<{ 
    name: string; 
    posts: number;
    badges?: Array<{
      id: string;
      name: string;
      icon: string;
      color: string;
      style?: string;
      animation?: string;
    }>;
  }>> {
    const response = await apiClient.get<{ contributors: Array<{ 
      name: string; 
      posts: number;
      badges?: Array<{
        id: string;
        name: string;
        icon: string;
        color: string;
        style?: string;
        animation?: string;
      }>;
    }> }>(
      `/public/stores/${storeId}/forum/top-contributors`
    )
    return response.contributors
  },

  async getForumBadges(storeId: string): Promise<Array<{ id: string; name: string; description: string; icon: string; color: string; type: string; style?: string; animation?: string; postsRequired?: number; topicsRequired?: number; reactionsRequired?: number }>> {
    const response = await apiClient.get<{ badges: Array<{ id: string; name: string; description: string; icon: string; color: string; type: string; style?: string; animation?: string }> }>(
      `/public/stores/${storeId}/forum/badges`
    )
    return response.badges
  },

  async getRecentBadgeEarners(storeId: string): Promise<Array<{ id: string; playerName: string; playerId: string; badgeName: string; badgeIcon: string; badgeColor: string; timeAgo: string }>> {
    const response = await apiClient.get<{ earners: Array<{ id: string; playerName: string; playerId: string; badgeName: string; badgeIcon: string; badgeColor: string; timeAgo: string }> }>(
      `/public/stores/${storeId}/forum/recent-badge-earners`
    )
    return response.earners
  },

  async getPlayerBadges(playerId: string): Promise<ForumBadge[]> {
    const response = await apiClient.get<{ badges: ForumBadge[] }>(
      `/public/stores/forum/players/${playerId}/badges`
    )
    return response.badges
  },
}

export default forumStoreService
